/**
 * TabAudioRecorder — admin modal that uses getDisplayMedia({audio:true}) to
 * capture audio from any tab the user is currently playing music in
 * (e.g. a YouTube tab, Spotify Web Player, Apple Music).
 *
 *   1. User clicks "Capture Tab Audio".
 *   2. Browser shows the system "Share a tab" dialog. User picks the tab
 *      that has the song playing (and ticks "Share tab audio").
 *   3. We record exactly `duration` seconds via MediaRecorder → Blob.
 *   4. Blob is POSTed as `audio` to /admin/shows/{id}/lipsync-from-upload
 *      so the existing Hedra regeneration pipeline runs.
 *
 * Notes:
 *   - Chrome + Edge support tab-audio capture. Firefox + Safari do NOT,
 *     so we degrade gracefully with a clear message.
 *   - Output is WebM/Opus — the backend transcodes via ffmpeg to MP3.
 */
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Mic, Square } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/http";

const RECORD_SECONDS = 10;

export default function TabAudioRecorder({ open, onClose, showId, artist, onJobStarted }) {
  const [phase, setPhase] = useState("idle"); // idle | sharing | recording | uploading
  const [elapsed, setElapsed] = useState(0);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const supportsTabCapture =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getDisplayMedia;

  // Cleanup on unmount or close
  useEffect(() => {
    if (!open) {
      cleanupStream();
      setPhase("idle");
      setElapsed(0);
    }
    return cleanupStream;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cleanupStream = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    streamRef.current = null;
    try { clearInterval(timerRef.current); } catch {}
    timerRef.current = null;
  };

  const startCapture = async () => {
    if (!supportsTabCapture) return;
    setPhase("sharing");
    try {
      // Ask for a screen/tab share with audio. Chrome lets the user pick a
      // single tab and tick "share tab audio" — that's exactly what we want.
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true, // required by spec; we discard the video track
        audio: true,
      });
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        stream.getTracks().forEach((t) => t.stop());
        setPhase("idle");
        toast.error("No tab audio detected. Re-share the tab and tick 'Share tab audio'.");
        return;
      }
      // Stop the video track immediately, keep audio only.
      stream.getVideoTracks().forEach((t) => t.stop());
      const audioOnly = new MediaStream(audioTracks);
      streamRef.current = audioOnly;

      const rec = new MediaRecorder(audioOnly, { mimeType: "audio/webm;codecs=opus" });
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        cleanupStream();
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 5_000) {
          toast.error("Recording too short / silent. Try again.");
          setPhase("idle");
          return;
        }
        await uploadRecording(blob);
      };
      rec.start(250);
      setPhase("recording");
      setElapsed(0);
      const startedAt = Date.now();
      timerRef.current = setInterval(() => {
        const sec = (Date.now() - startedAt) / 1000;
        setElapsed(sec);
        if (sec >= RECORD_SECONDS) {
          try { rec.stop(); } catch {}
          clearInterval(timerRef.current);
        }
      }, 100);
    } catch (e) {
      cleanupStream();
      setPhase("idle");
      if (e?.name !== "NotAllowedError") {
        toast.error(`Tab capture failed: ${e?.message || e}`);
      }
    }
  };

  const stopEarly = () => {
    if (recorderRef.current && recorderRef.current.state === "recording") {
      try { recorderRef.current.stop(); } catch {}
    }
    try { clearInterval(timerRef.current); } catch {}
  };

  const uploadRecording = async (blob) => {
    setPhase("uploading");
    const fd = new FormData();
    fd.append("audio", new File([blob], `${artist || "tab"}-capture.webm`, { type: "audio/webm" }));
    fd.append("duration_seconds", String(Math.max(4, Math.min(30, Math.round(elapsed || RECORD_SECONDS)))));
    try {
      const { data } = await http.post(`/admin/shows/${showId}/lipsync-from-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Real-track lip-sync started for ${artist} (~90s).`);
      if (typeof onJobStarted === "function") onJobStarted(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
      setPhase("idle");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4" data-testid="tab-audio-recorder">
      <div className="absolute inset-0 bg-black/90 backdrop-blur" onClick={phase === "recording" || phase === "uploading" ? null : onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative w-full max-w-lg border border-[#00E5FF]/40 bg-[#0B0608] p-6 md:p-8"
      >
        <button
          onClick={onClose}
          disabled={phase === "recording" || phase === "uploading"}
          className="absolute top-4 right-4 w-9 h-9 rounded-full border border-white/25 bg-black/60 flex items-center justify-center text-white/80 hover:text-white hover:border-[#FF007F] disabled:opacity-40"
          data-testid="tab-audio-recorder-close"
        >
          <X size={16} />
        </button>

        <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[#00E5FF] inline-flex items-center gap-2">
          <span className="ring-dot" /> Lip-sync · Tab Audio
        </div>
        <h3 className="mt-3 font-display font-black tracking-tighter text-3xl md:text-4xl">
          Record real song from a tab.
        </h3>
        <p className="mt-2 text-white/65 text-sm leading-relaxed">
          Open the song on YouTube / Spotify / Apple Music in another tab and start playing it.
          Click <span className="text-[#00E5FF]">Capture</span> below, pick that tab, tick
          <span className="text-[#00E5FF]"> &ldquo;Share tab audio&rdquo;</span>, then click Share.
          We&rsquo;ll record {RECORD_SECONDS}s and re-lip-sync <span className="text-[#FFD166]">{artist}</span>&rsquo;s hologram to it.
        </p>

        {!supportsTabCapture && (
          <div className="mt-4 border border-[#FF007F]/40 bg-[#FF007F]/10 text-[#FF007F] p-3 font-mono-ui text-[10px] tracking-[0.18em] uppercase">
            This browser can&rsquo;t capture tab audio. Use Chrome or Edge.
          </div>
        )}

        {phase === "recording" && (
          <div className="mt-6 border border-[#FF007F]/40 bg-[#FF007F]/10 p-4" data-testid="tab-audio-recording">
            <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#FF007F] inline-flex items-center gap-2">
              <span className="ring-dot" /> Recording · {elapsed.toFixed(1)}s / {RECORD_SECONDS}s
            </div>
            <div className="mt-3 h-1.5 bg-black/60 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#00E5FF] to-[#FF007F] transition-all" style={{ width: `${Math.min(100, (elapsed / RECORD_SECONDS) * 100)}%` }} />
            </div>
            <button onClick={stopEarly} className="btn-ghost text-[10px] py-2 px-3 mt-3 inline-flex items-center gap-2" data-testid="tab-audio-stop-early">
              <Square size={11} /> Stop now & upload
            </button>
          </div>
        )}

        {phase === "uploading" && (
          <div className="mt-6 font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[#00E5FF] inline-flex items-center gap-2" data-testid="tab-audio-uploading">
            <span className="ring-dot" /> Uploading & queueing Hedra…
          </div>
        )}

        {(phase === "idle" || phase === "sharing") && (
          <button
            onClick={startCapture}
            disabled={!supportsTabCapture || phase === "sharing"}
            className="btn-neon mt-6 w-full disabled:opacity-60"
            data-testid="tab-audio-capture-btn"
          >
            <Mic size={14} className="mr-2 inline" /> {phase === "sharing" ? "Pick a tab…" : "Capture from Tab Audio"}
          </button>
        )}
      </motion.div>
    </div>
  );
}
